/**
 * An item's parent path, UPWARD in the decomposition DAG (docs/RELATIONSHIPS.md:
 * `parent` is multi-parent and acyclic), as a pure walk over gathered edges.
 * One rule for every surface that names where a row sits - the Work list
 * row's parent path, a saved list, a search hit's breadcrumb, an item record -
 * so a row and its breadcrumb can never disagree about "the" parent.
 *
 * The rule (Leon, 2026-09-15): a row's `parents` are every parent the viewer
 * may see, oldest edge first; the FIRST of them is the representative parent,
 * and `path` is that parent's own chain walked up the same way, root first. A
 * parent the viewer cannot see is omitted and never counted - the gate answers
 * by omission, never with a stub - and the walk up continues through the next
 * visible edge rather than stopping, because an invisible parent is, to this
 * viewer, no parent at all.
 *
 * Pure: the caller gathers the edges (in edge-age order) and the titles of
 * exactly the items the viewer may see; a seed with no title is omitted from
 * the result (the item itself is invisible - never leak its parent path).
 */

export interface Crumb {
  id: string;
  title: string;
}

/** One active `parent` edge, child -> parent. The caller orders the list by
 *  edge age (oldest first) so "the first parent" is deterministic per load. */
export interface ParentEdge {
  childId: string;
  parentId: string;
}

export interface ParentPath {
  /** Every visible parent, oldest edge first. Empty for a root. */
  parents: Crumb[];
  /** The representative parent's chain: ROOT first, the immediate parent
   *  (`parents[0]`) last. Empty when there is no visible parent. */
  path: Crumb[];
}

export function parentPathsOf(
  seeds: readonly string[],
  edges: readonly ParentEdge[],
  titleOf: ReadonlyMap<string, string>,
): Map<string, ParentPath> {
  // Visible parents per child, in the edges' order, deduped (a DAG has one
  // edge per pair, but a gathered closure may repeat an edge by path).
  const parentsOf = new Map<string, string[]>();
  for (const e of edges) {
    if (!titleOf.has(e.parentId)) continue;
    const list = parentsOf.get(e.childId) ?? parentsOf.set(e.childId, []).get(e.childId)!;
    if (!list.includes(e.parentId)) list.push(e.parentId);
  }
  const crumb = (id: string): Crumb => ({ id, title: titleOf.get(id)! });

  const out = new Map<string, ParentPath>();
  for (const id of new Set(seeds)) {
    if (!titleOf.has(id)) continue; // the item itself is invisible: omit, never leak
    const parents = (parentsOf.get(id) ?? []).map(crumb);
    const path: Crumb[] = [];
    const seen = new Set([id]);
    let cur = parents[0]?.id;
    while (cur !== undefined && !seen.has(cur)) {
      seen.add(cur);
      path.unshift(crumb(cur));
      cur = (parentsOf.get(cur) ?? []).find((p) => !seen.has(p));
    }
    out.set(id, { parents, path });
  }
  return out;
}

/**
 * Every VISIBLE ancestor of each visible seed: the whole upward closure over
 * every visible parent, not only the representative chain `parentPathsOf` walks.
 * "Is this item under that one" is a question about the DAG, and an item with
 * two parents is under both. The same visibility rule as `parentPathsOf`: a
 * parent the viewer cannot see is no parent at all, so the walk up never passes
 * through one, and an invisible seed is absent from the result.
 *
 * Pure: the caller gathers the edges and the set of ids the viewer may see.
 */
export function ancestorSetsOf(
  seeds: readonly string[],
  edges: readonly ParentEdge[],
  visible: ReadonlySet<string>,
): Map<string, Set<string>> {
  const parentsOf = new Map<string, string[]>();
  for (const e of edges) {
    if (!visible.has(e.parentId)) continue;
    const list = parentsOf.get(e.childId) ?? parentsOf.set(e.childId, []).get(e.childId)!;
    if (!list.includes(e.parentId)) list.push(e.parentId);
  }
  const out = new Map<string, Set<string>>();
  for (const id of new Set(seeds)) {
    if (!visible.has(id)) continue;
    const above = new Set<string>();
    const queue = [...(parentsOf.get(id) ?? [])];
    while (queue.length > 0) {
      const next = queue.pop()!;
      if (next === id || above.has(next)) continue;
      above.add(next);
      queue.push(...(parentsOf.get(next) ?? []));
    }
    out.set(id, above);
  }
  return out;
}

/**
 * **A closed line with open work under it keeps its place** (Leon,
 * 2026-09-22): which CLOSED rows a surface that hides closed work must show
 * anyway, because an open row it shows sits below them.
 *
 * The case is a checklist category: tick "Küche" and it closes, but its two
 * open lines are still someone's work. Hiding the category would strand them
 * at the top of the rail with no section to sit in, and the person who just
 * ticked would watch their list rearrange itself. So the category stays,
 * struck through like any ticked line, with its open children under it; when
 * nothing open remains below, it falls behind "Show N closed" like any other
 * finished line.
 *
 * Derived from the rows and the edges alone - there is no flag, and nothing is
 * stored. The walk goes UP from every open row through its parent edges and
 * collects the closed ones it passes, so a closed grandparent of an open row is
 * kept as surely as its closed parent. Cycles cannot loop it (the spine refuses
 * them, and a read must not trust that): a row is visited once.
 */
export function closedOverOpen(
  rows: readonly { id: string; closed: boolean }[],
  edges: readonly ParentEdge[],
): Set<string> {
  const closed = new Map(rows.map((r) => [r.id, r.closed]));
  const parentsOf = new Map<string, string[]>();
  for (const e of edges) {
    if (!closed.has(e.childId) || !closed.has(e.parentId)) continue;
    const list = parentsOf.get(e.childId) ?? parentsOf.set(e.childId, []).get(e.childId)!;
    if (!list.includes(e.parentId)) list.push(e.parentId);
  }
  const keep = new Set<string>();
  const seen = new Set<string>();
  const walkUp = (id: string): void => {
    for (const parent of parentsOf.get(id) ?? []) {
      if (seen.has(parent)) continue;
      seen.add(parent);
      if (closed.get(parent)) keep.add(parent);
      walkUp(parent);
    }
  };
  for (const r of rows) {
    if (r.closed) continue;
    walkUp(r.id);
  }
  return keep;
}

/**
 * **The nesting a filtered set keeps** (docs/CHECKLIST.md §3, the rail's
 * criteria, 2026-09-27): given the checklist's whole nesting (`under`: each
 * row's parents within the set) and the rows a filter KEPT, each kept row sits
 * under its NEAREST kept ancestors. A parent the filter took out hands its
 * place to its own parents, as far up as it takes; a row with no kept ancestor
 * is top level. So `closed: no` hides a struck category and its open lines
 * stay, drawn under the next row up that is still there - dropping them would
 * make the filter lose the lines it was asked for.
 *
 * Pure, over the nesting the read already built. A row is visited once per
 * walk up, so a cycle cannot loop it and a parent reached twice is listed once,
 * in first-seen order.
 */
export function nearestKeptParents(
  under: Readonly<Record<string, readonly string[]>>,
  kept: ReadonlySet<string>,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const id of kept) {
    const parents: string[] = [];
    const seen = new Set([id]);
    const walkUp = (ids: readonly string[]): void => {
      for (const p of ids) {
        if (seen.has(p)) continue;
        seen.add(p);
        if (kept.has(p)) parents.push(p);
        else walkUp(under[p] ?? []);
      }
    };
    walkUp(under[id] ?? []);
    if (parents.length > 0) out[id] = parents;
  }
  return out;
}

/**
 * Each parent's order of its LIST, narrowed the same way as
 * {@link nearestKeptParents}: a child the filter took out is replaced, in its
 * own place, by its children's order, so a child lifted to its grandparent
 * sits where its old parent sat. A LINE (`isLine`, docs/CHECKLIST.md §2) is
 * never filtered and never lifted: it stays on its own item's list, and the
 * lines of a child the filter took out leave with it. Only `parents` keep an
 * entry (the kept rows, and the list's root, whose list is a rooted
 * checklist's top level). Pure.
 */
export function narrowedChildOrder(
  childOrder: Readonly<Record<string, readonly string[]>>,
  kept: ReadonlySet<string>,
  parents: Iterable<string>,
  isLine: (id: string) => boolean = () => false,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const parentId of parents) {
    const list: string[] = [];
    const seen = new Set([parentId]);
    const walk = (ids: readonly string[], own: boolean): void => {
      for (const c of ids) {
        if (seen.has(c)) continue;
        seen.add(c);
        if (isLine(c)) {
          if (own) list.push(c);
        } else if (kept.has(c)) list.push(c);
        else walk(childOrder[c] ?? [], false);
      }
    };
    walk(childOrder[parentId] ?? [], true);
    if (list.length > 0) out[parentId] = list;
  }
  return out;
}
